/**
 * Taking a backup, and putting one back.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * 1.6.2 had a Backup button. DNR Vyapar 2.0 did not, for a long time — and for
 * an app whose whole purpose is being the record of invoices a business issued,
 * that made it unfit for real users. Every invoice lived in one browser profile,
 * and "recovering" it meant "do not clear your browsing data". This is the
 * missing capability, and it is a release requirement, not a nicety.
 *
 * THE THREE THINGS THAT ARE EASY TO GET WRONG
 * --------------------------------------------
 *  1. **A restore must be atomic.** A failure halfway through must leave the
 *     database exactly as it was. It runs in ONE transaction over every table it
 *     touches, and the pre-restore snapshot is taken inside that same
 *     transaction — so a thrown error rolls the snapshot back too, and a failed
 *     restore cannot leave behind a "backup" of the state it just destroyed.
 *
 *  2. **Validation happens BEFORE the transaction opens.** Reading a file is
 *     cheap and touches nothing. Discovering it is from a future version after
 *     clearing twenty tables is not recoverable by the user.
 *
 *  3. **Restoring is scoped to the businesses in the file.** A user with two
 *     companies who restores a backup containing one must not silently lose the
 *     other. That is the isolation rule, applied to the most destructive write in
 *     the application.
 */
import type { DNRDatabase } from '../db/database';
import { TABLE_NAMES } from '../db/database';
import { readDataVersion } from '../db/migrations';
import { ulid } from '../../platform/ids';
import {
  BACKUP_FORMAT,
  BACKUP_FORMAT_VERSION,
  BACKUP_TABLES,
  backupFileName,
  validateBackup,
  type BackupFile,
  type BackupTable,
} from './backupFormat';

export type BackupErrorCode =
  | 'INVALID_BACKUP'
  | 'FUTURE_FORMAT'
  | 'CONFIRMATION_REQUIRED'
  | 'NOTHING_TO_RESTORE';

export class BackupError extends Error {
  constructor(
    readonly code: BackupErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'BackupError';
  }
}

/** Every table a restore is allowed to touch. */
const RESTORE_TABLES: readonly BackupTable[] = BACKUP_TABLES;

export interface CreateBackupOptions {
  /** Restrict to one company. Omit for every company. */
  readonly businessId?: string;
  /** Injected in tests; never read from a clock inside the service. */
  readonly now: string;
}

export interface BackupSummary {
  readonly fileName: string;
  readonly json: string;
  readonly businessIds: readonly string[];
  readonly rowCount: number;
  readonly dataVersion: number;
}

function businessIdOf(row: unknown): string | null {
  if (typeof row !== 'object' || row === null) return null;
  const id = (row as { businessId?: unknown }).businessId;
  return typeof id === 'string' ? id : null;
}

/** A row's primary key, or null when the shape cannot supply one. */
function rowId(row: unknown): string | null {
  if (typeof row !== 'object' || row === null) return null;
  const id = (row as { id?: unknown }).id;
  return typeof id === 'string' ? id : null;
}

/**
 * Does this row belong to the businesses being restored?
 *
 * `businesses` is the special case, and getting it wrong is a total failure: a
 * business row carries `id`, not `businessId`, so a filter that looks only for
 * `businessId` never matches it, the row survives the clear, and the re-insert
 * collides with its own primary key. The whole restore then aborts.
 *
 * `null` means "not scoped to a company" — `settings` and similar — which is
 * why those tables are restored without being cleared.
 */
function rowInScope(table: string, row: unknown, scope: readonly string[]): boolean {
  const id = table === 'businesses'
    ? (row as { id?: unknown }).id
    : businessIdOf(row);
  return typeof id === 'string' && scope.includes(id);
}

/**
 * Take a backup of everything, or of one company.
 *
 * Rows are filtered by `businessId` where the table has one, and copied whole
 * where it does not. The one table copied whole by DESIGN is `settings`: it holds
 * the active-business preference and the data version, and carrying those from
 * the machine that took the backup would repoint a restored install at a company
 * id that does not exist on the machine restoring it.
 */
export async function createBackup(
  db: DNRDatabase,
  options: CreateBackupOptions,
): Promise<BackupSummary> {
  const businesses = await db.businesses.toArray();
  const included = options.businessId
    ? businesses.filter((b) => b.id === options.businessId).map((b) => b.id)
    : businesses.map((b) => b.id);

  const tables: Record<string, unknown[]> = {};
  let rowCount = 0;

  for (const name of RESTORE_TABLES) {
    let rows: unknown[] = await db.table(name).toArray();
    if (name !== 'settings' && options.businessId) {
      // `rowInScope`, not a `businessId` test: a business row is keyed by `id`, so
      // a filter that only looks for `businessId` keeps BOTH companies and the
      // "one company" backup silently contains two.
      rows = rows.filter((row) => rowInScope(name, row, [options.businessId as string]));
    }
    tables[name] = rows;
    rowCount += rows.length;
  }

  const file: BackupFile = {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    app: 'DNR_Vyapar_2',
    dataVersion: await readDataVersion(db),
    takenAt: options.now,
    businessIds: included,
    // Built table by table above, so the cast is a widening rather than a lie:
    // every key in BACKUP_TABLES was assigned, and `validateBackup` has already
    // confirmed each value is an array.
    tables: tables as unknown as BackupFile['tables'],
  };

  const label = options.businessId
    ? (businesses.find((b) => b.id === options.businessId)?.legalName ?? 'business')
    : 'all-businesses';

  return {
    fileName: backupFileName(options.now, label),
    json: JSON.stringify(file, null, 2),
    businessIds: included,
    rowCount,
    dataVersion: file.dataVersion,
  };
}


export interface RestoreOptions {
  /**
   * MUST be true. There is no default: a restore is destructive, and a service
   * whose dangerous path is reachable by forgetting an argument is a service that
   * will eventually delete a shop's invoices.
   */
  readonly confirmed: boolean;
  /** Restrict to these businesses. Defaults to every business in the file. */
  readonly businessIds?: readonly string[];
  readonly now: string;
}

export interface RestoreSummary {
  readonly restored: Readonly<Record<string, number>>;
  readonly businesses: number;
  readonly rowsRemoved: number;
}

/**
 * Restore a validated backup, in one transaction.
 *
 * The order is: validate, open the transaction, snapshot, clear the scope, write.
 * A failure at any step rolls the whole thing back, including the snapshot.
 */
export async function restoreBackup(
  db: DNRDatabase,
  backup: unknown,
  options: RestoreOptions,
): Promise<RestoreSummary> {
  // 1. Validate BEFORE opening a transaction. Reading a file must never be able
  //    to leave a database half-cleared.
  const validation = validateBackup(backup);
  if (!validation.ok) {
    throw new BackupError(
      validation.problem === 'FUTURE_FORMAT' ? 'FUTURE_FORMAT' : 'INVALID_BACKUP',
      validation.message ?? 'That file is not a usable DNR Vyapar backup.',
    );
  }

  // 2. The confirmation gate, deliberately not optional and not defaulted.
  if (options.confirmed !== true) {
    throw new BackupError(
      'CONFIRMATION_REQUIRED',
      'Restoring replaces the data in this app. It has not been confirmed.',
    );
  }

  const file = backup as BackupFile;
  const scope = options.businessIds ?? file.businessIds;

  return (await db.transaction('rw', TABLE_NAMES.map((n) => db.table(n)), async () => {
    // 3. Snapshot, INSIDE the transaction. A snapshot written outside it would
    //    survive a failed restore — the worst of both outcomes: no restore, and a
    //    stale "backup" that is not what the user had a moment ago.
    const preRestore = new Map<string, unknown[]>();
    for (const name of RESTORE_TABLES) {
      preRestore.set(name, await db.table(name).toArray());
    }

    try {
      const restored: Record<string, number> = {};
      let rowsRemoved = 0;

      for (const name of RESTORE_TABLES) {
        const table = db.table(name);
        const incoming = file.tables[name] ?? [];

        // Clear only the scope being restored. A two-company install restoring a
        // one-company backup KEEPS its other company: the isolation rule,
        // applied to the most destructive write in the application.
        if (scope.length > 0) {
          const existing = await table.toArray();
          const removable = existing.filter((row) => rowInScope(name, row, scope));
          if (removable.length) {
            await table.bulkDelete(removable.map((r) => (r as { id: string }).id));
          }
          rowsRemoved += removable.length;

          // A row the scope clear KEPT but the file also carries would make
          // `bulkAdd` abort on its own primary key and roll back the entire
          // restore — seen as Dexie's BulkError "Key already exists". This is
          // not hypothetical: `settings` rows (`app:activeBusinessId`,
          // `app:deviceId`) carry no businessId, so they always survive the
          // clear, and every real install has them. The file wins for the rows
          // it contains — replace them — while everything it does not mention
          // stays untouched.
          const incomingIds = new Set(
            incoming
              .map((row) => rowId(row))
              .filter((id): id is string => id !== null),
          );
          const shadowed = existing
            .filter((row) => !rowInScope(name, row, scope))
            .map((row) => rowId(row))
            .filter((id): id is string => id !== null && incomingIds.has(id));
          if (shadowed.length) await table.bulkDelete(shadowed);
        } else {
          await table.clear();
          rowsRemoved += preRestore.get(name)?.length ?? 0;
        }

        if (incoming.length) await table.bulkAdd(incoming as never[]);
        restored[name] = incoming.length;
      }

      // 4. Audit the restore itself. A restore that is not recorded is a restore
      //    nobody can investigate, and this is the write most worth investigating.
      await db.auditLogs.add({
        id: ulid(),
        entityType: 'BACKUP',
        entityId: 'restore',
        action: 'RESTORE',
        after: JSON.stringify({
          takenAt: file.takenAt,
          dataVersion: file.dataVersion,
          businessIds: scope,
          rowsRemoved,
        }),
        userId: 'local',
        userName: 'Restore',
        occurredAt: options.now,
      });

      return { restored, businesses: file.businessIds.length, rowsRemoved };
    } catch (error) {
      // The transaction already rolls back; restoring explicitly makes the intent
      // obvious to the next reader and survives a future widening of the scope.
      for (const name of RESTORE_TABLES) {
        const table = db.table(name);
        await table.clear();
        const rows = preRestore.get(name) ?? [];
        if (rows.length) await table.bulkAdd(rows as never[]);
      }
      throw error;
    }
  })) as RestoreSummary;
}

/** The data version this install is at, for the backup screen to show. */
export async function currentDataVersion(db: DNRDatabase): Promise<number> {
  return readDataVersion(db);
}

